/**
 * Text ↔ number for the promotion form's number inputs.
 *
 * The inputs used to be `type="number"` and controlled by the parsed number: every keystroke was
 * parsed and the number written back as the input's value. "1." parses to 1, so the "." vanished
 * on the next render and the cursor jumped — typing "1.50" produced "501" (Bakhrom, 1st Phorm,
 * 2026-10-15). The input now keeps the text the rep typed and only reports the number it means;
 * the text is replaced only when the value changes from outside (e.g. "Use as planned amount").
 */

/** Characters a number field accepts while typing: digits, one ".", a leading "-", "," and "$". */
const PARTIAL_NUMBER = /^-?[\d,]*\.?\d*$/

/** PURE: whether `text` is something a rep can be part-way through typing in a number field. */
export function is_partial_number_text(text: string): boolean {
  return PARTIAL_NUMBER.test(text.replace(/[\s$]/g, ""))
}

/** PURE: the number `text` means, or undefined when it means none yet ("", ".", "-"). */
export function parse_number_text(text: string): number | undefined {
  const cleaned = text.replace(/[\s$,]/g, "")
  if (!cleaned || cleaned === "." || cleaned === "-" || cleaned === "-.") return undefined
  const value = Number(cleaned)
  return Number.isFinite(value) ? value : undefined
}

/** PURE: the text to show for a value set from outside the input. */
export function number_to_text(value: number | undefined | null): string {
  return value == null || !Number.isFinite(value) ? "" : String(value)
}

/**
 * PURE: a value as the input shows it once the rep leaves the field — thousands separated and at
 * most two decimals, so 7500 reads "7,500" and 1.5 reads "1.50" in a money field
 * (Bakhrom: "Planned Amount is bare. It has no $ prefix or thousands separators").
 */
export function grouped_number_text(value: number | undefined | null, money = false): string {
  if (value == null || !Number.isFinite(value)) return ""
  return value.toLocaleString("en-US", { minimumFractionDigits: money ? 2 : 0, maximumFractionDigits: money ? 2 : 4 })
}

/**
 * PURE: the text the input should show after its value prop changed — the rep's own text when it
 * already means that value (so "1.50" is not rewritten to "1.5" mid-edit), the value otherwise.
 */
export function text_for_value(current_text: string, value: number | undefined | null): string {
  const current = parse_number_text(current_text)
  const next = value == null || !Number.isFinite(value) ? undefined : value
  return current === next ? current_text : number_to_text(next)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("typing a decimal", () => {
    it("keeps every keystroke of 1.50, reporting the number each step means", () => {
      // Replays the keystrokes the way the input sees them: the text after each key, then the
      // value the form stores, then the text the input shows on the next render.
      let text = ""
      const reported: Array<number | undefined> = []
      for (const key of ["1", ".", "5", "0"]) {
        const typed = text + key
        expect(is_partial_number_text(typed)).toBe(true)
        const value = parse_number_text(typed)
        reported.push(value)
        text = text_for_value(typed, value)
      }
      expect(text).toBe("1.50")
      expect(reported).toEqual([1, 1, 1.5, 1.5])
    })

    it("keeps a lone leading point", () => {
      expect(text_for_value(".", parse_number_text("."))).toBe(".")
      expect(parse_number_text(".5")).toBe(0.5)
    })
  })

  describe("parse_number_text", () => {
    it("reads thousands separators and a dollar sign", () => {
      expect(parse_number_text("7,500")).toBe(7500)
      expect(parse_number_text("$1.50")).toBe(1.5)
      expect(parse_number_text(" 0 ")).toBe(0)
    })

    it("means nothing for an empty or unfinished entry", () => {
      expect(parse_number_text("")).toBeUndefined()
      expect(parse_number_text("-")).toBeUndefined()
      expect(parse_number_text("abc")).toBeUndefined()
    })
  })

  describe("is_partial_number_text", () => {
    it("refuses letters and a second point", () => {
      expect(is_partial_number_text("1.5.")).toBe(false)
      expect(is_partial_number_text("1a")).toBe(false)
      expect(is_partial_number_text("-12,000.25")).toBe(true)
      expect(is_partial_number_text("")).toBe(true)
    })
  })

  describe("grouped_number_text", () => {
    it("groups thousands and pads money to cents", () => {
      expect(grouped_number_text(7500, true)).toBe("7,500.00")
      expect(grouped_number_text(1.5, true)).toBe("1.50")
      expect(grouped_number_text(10000)).toBe("10,000")
      expect(grouped_number_text(undefined)).toBe("")
      // The grouped text still reads back as the same number.
      expect(parse_number_text(grouped_number_text(1234567.891, true))).toBe(1234567.89)
    })
  })

  describe("text_for_value", () => {
    it("shows a value set from outside", () => {
      expect(text_for_value("", 7500)).toBe("7500")
      expect(text_for_value("1.5", undefined)).toBe("")
    })

    it("shows zero instead of blanking it", () => {
      expect(text_for_value("", 0)).toBe("0")
    })
  })
}
